import { isMyMessage, isVoiceMessage } from '@sendbird/uikit-utils';
import { useSendbirdChat } from '@sendbird/uikit-react-native';
import { useDocumentDownload } from 'app/_modules/history';
import type { TChatAttachment, TChatMessageViewKind, TUseChatMessageViewParams } from '../types';
import { getMessageLabel, isImageAttachment } from '../utils/channelPreview';
import { parseChatDocument } from '../utils/chatDocument';
import { isWelcomeMessage } from '../utils/welcomeMessage';
import { useMessageLabels } from './useMessageLabels';

export const useChatMessageView = ({ message, prevMessage: rawPrevMessage, nextMessage }: TUseChatMessageViewParams) => {
    const { currentUser } = useSendbirdChat();
    const labels = useMessageLabels();
    const document = message.isUserMessage() ? parseChatDocument(message.data) : null;
    const { openDocument, downloadingId } = useDocumentDownload(document?.pathwayId ?? '');

    const prevMessage = isWelcomeMessage(rawPrevMessage) ? undefined : rawPrevMessage;
    const isMine = isMyMessage(message, currentUser?.userId);
    const onPressDocument = document
        ? () => openDocument(document.documentId, document.fileName, document.fileType)
        : undefined;

    if (isWelcomeMessage(message)) {
        const kind: TChatMessageViewKind = !rawPrevMessage && !nextMessage ? 'emptyConversation' : 'hidden';
        return { kind, prevMessage, isMine, attachment: null, onPressDocument };
    }

    const isPlainSucceeded =
        (message.isUserMessage() || message.isFileMessage()) &&
        message.sendingStatus === 'succeeded' &&
        !message.parentMessageId &&
        !message.threadInfo?.replyCount &&
        !message.reactions?.length;
    const isCustomText =
        message.isUserMessage() && isPlainSucceeded && !message.ogMetaData && !message.mentionedUsers?.length;
    const hasAttachment = Boolean(document) || (message.isFileMessage() && isPlainSucceeded && !isVoiceMessage(message));
    const attachment: TChatAttachment | null = hasAttachment
        ? {
              label: getMessageLabel(message, labels),
              isImage: isImageAttachment(message),
              isOpening: Boolean(document && downloadingId === document.documentId),
          }
        : null;

    let kind: TChatMessageViewKind = 'native';
    if (message.isAdminMessage()) kind = 'admin';
    else if (isCustomText || attachment) kind = 'bubble';

    return { kind, prevMessage, isMine, attachment, onPressDocument };
};
